import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { supabase } from "@/shared/lib/supabase";
import { useQuery } from "@tanstack/react-query";

interface SearchResult {
  estate_id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  account_holder_name: string;
  account_holder_email: string;
  document_count: number;
  task_count: number;
  created_at: string;
}

export default function GlobalSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search API
  const { data: results, isLoading } = useQuery({
    queryKey: ["admin-global-search", debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery.trim()) return [];

      // Search users by email, first name, last name, or full name
      // First, try searching by individual fields
      const { data: users } = await supabase
        .from("users")
        .select("user_id, email, first_name, last_name")
        .or(
          `email.ilike.%${debouncedQuery}%,first_name.ilike.%${debouncedQuery}%,last_name.ilike.%${debouncedQuery}%`,
        )
        .limit(20);

      let allUsers = users || [];

      // If searching with a space (likely full name), also search for combinations
      if (debouncedQuery.includes(" ") && allUsers.length < 20) {
        const nameParts = debouncedQuery.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          const firstName = nameParts[0];
          const lastName = nameParts.slice(1).join(" ");

          // Search for users where first name matches first part and last name matches second part
          const { data: fullNameUsers } = await supabase
            .from("users")
            .select("user_id, email, first_name, last_name")
            .ilike("first_name", `%${firstName}%`)
            .ilike("last_name", `%${lastName}%`)
            .limit(20);

          if (fullNameUsers && fullNameUsers.length > 0) {
            // Merge results, avoiding duplicates
            const existingIds = new Set(allUsers.map((u) => u.user_id));
            const newUsers = fullNameUsers.filter((u) => !existingIds.has(u.user_id));
            allUsers = [...allUsers, ...newUsers].slice(0, 20);
          }
        }
      }

      if (!allUsers || allUsers.length === 0) return [];

      // Get persons (decedents) associated with these users
      const { data: userEstates } = await supabase
        .from("user_estates")
        .select("estate_id, user_id")
        .in(
          "user_id",
          allUsers.map((u) => u.user_id),
        );

      if (!userEstates || userEstates.length === 0) return [];

      // Get decedent details
      const { data: persons } = await supabase
        .from("estates")
        .select("estate_id, full_name, first_name, last_name, created_at")
        .in(
          "estate_id",
          userEstates.map((up) => up.estate_id),
        )
        .eq("estate_type", "deceased")
        .limit(10);

      if (!persons || persons.length === 0) return [];

      // Get additional data for each person
      const results: SearchResult[] = await Promise.all(
        persons.map(async (person) => {
          // Get account holder info (first user associated with this person)
          const { data: userEstates } = await supabase
            .from("user_estates")
            .select("user_id")
            .eq("estate_id", person.estate_id)
            .limit(1);

          let accountHolderName = "Unknown";
          let accountHolderEmail = "No email";

          if (userEstates && userEstates.length > 0) {
            const { data: user } = await supabase
              .from("users")
              .select("email, first_name, last_name")
              .eq("user_id", userEstates[0].user_id)
              .single();

            if (user) {
              accountHolderName =
                user.first_name && user.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user.first_name || user.last_name || "Unknown";
              accountHolderEmail = user.email || "No email";
            }
          }

          // Get document count
          const { count: docCount } = await supabase
            .from("estate_documents")
            .select("*", { count: "exact", head: true })
            .eq("estate_id", person.estate_id);

          // Get task count
          const { count: taskCount } = await supabase
            .from("task_submissions")
            .select("*", { count: "exact", head: true })
            .eq("estate_id", person.estate_id);

          return {
            estate_id: person.estate_id,
            full_name: person.full_name || `${person.first_name} ${person.last_name}`,
            first_name: person.first_name,
            last_name: person.last_name,
            account_holder_name: accountHolderName,
            account_holder_email: accountHolderEmail,
            document_count: docCount || 0,
            task_count: taskCount || 0,
            created_at: person.created_at,
          };
        }),
      );

      return results;
    },
    enabled: debouncedQuery.trim().length >= 2,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Open dropdown when there's a query
  useEffect(() => {
    if (debouncedQuery.trim().length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [debouncedQuery]);

  const handleSelectResult = (estateId: number) => {
    navigate(`/admin/decedent/${estateId}`);
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <Input
          type="search"
          placeholder="Search by account holder email or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-4"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Searching...
            </div>
          ) : results && results.length > 0 ? (
            <div className="py-2">
              {results.map((result) => (
                <button
                  key={result.estate_id}
                  onClick={() => handleSelectResult(result.estate_id)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900">{result.account_holder_name}</div>
                  <div className="text-sm text-gray-500">{result.account_holder_email}</div>
                  <div className="text-sm text-gray-400 mt-1">Decedent: {result.full_name}</div>
                </button>
              ))}
            </div>
          ) : debouncedQuery.trim().length >= 2 ? (
            <div className="p-4 text-center text-gray-500">No results found</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
